/* [EZ] Header — interactions: mega/dropdown panels (hover-intent + keyboard),
   search overlay, mobile drawer + accordions. Carousel handled by ez-carousel.js. */
(function () {
  'use strict';

  const HOVER_CLOSE_DELAY = 180; // ms — lets the pointer cross gaps without flicker

  class EZHeader extends HTMLElement {
    connectedCallback() {
      this.closeTimer = null;
      this.openItem = null;
      this.bindPanels();
      this.bindSearch();
      this.bindDrawer();
      this.bindHeight();
      this.bindOverlay();
      this.bindLogo();
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') {
          this.closeAll();
          if (this.__setSearching) this.__setSearching(false);
        }
      });
      // Close any open panel when pointing/tapping outside the header (covers
      // click-opened dropdowns — e.g. the account menu — on touch + desktop).
      document.addEventListener('pointerdown', (e) => {
        if (this.openItem && !this.contains(e.target)) this.closeAll();
      });
    }

    // Publish the header height so the hero can pull up under it
    bindHeight() {
      const set = () => document.documentElement.style.setProperty('--ez-header-height', this.offsetHeight + 'px');
      set();
      window.addEventListener('resize', set);
      if (window.ResizeObserver) new ResizeObserver(set).observe(this);
    }

    // Transparent over the hero on the homepage; solid once scrolled past it
    bindOverlay() {
      if (this.dataset.overlay !== 'true') return;
      const hero = document.querySelector('[data-ez-hero]');
      const update = () => {
        const threshold = hero ? Math.max(0, hero.offsetHeight - this.offsetHeight) : 0;
        this.classList.toggle('is-solid', window.scrollY > threshold);
      };
      update();
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
    }

    // Per-banner logo swap (homepage). The hero tells us which logo the active slide
    // wants via the ez:hero:logo event; we also read the initial active slide on connect
    // (its .is-active class is set server-side, so there's no startup race). CSS only
    // honours .ez-header--logo-light while transparent over the hero.
    bindLogo() {
      const apply = (variant) => {
        const light = variant === 'light';
        this.classList.toggle('ez-header--logo-light', light);
        this.classList.toggle('ez-header--logo-dark', !light);
      };
      const hero = document.querySelector('[data-ez-hero]');
      if (hero) {
        const active = hero.querySelector('[data-slide].is-active') || hero.querySelector('[data-slide]');
        if (active && active.dataset.headerLogo) apply(active.dataset.headerLogo);
      }
      document.addEventListener('ez:hero:logo', (e) => {
        if (e.detail && e.detail.variant) apply(e.detail.variant);
      });
    }

    bindPanels() {
      const items = this.querySelectorAll('[data-mega], [data-dropdown]');
      items.forEach((item) => {
        const trigger = item.querySelector('[data-mega-trigger], [data-dropdown-trigger]');
        const panel = item.querySelector('[data-mega-panel], [data-dropdown-panel]');
        if (!trigger || !panel) return;

        item.addEventListener('mouseenter', () => this.open(item, trigger, panel));
        item.addEventListener('mouseleave', () => this.scheduleClose());
        trigger.addEventListener('focus', () => this.open(item, trigger, panel));
        trigger.addEventListener('click', (e) => {
          // A linked trigger (real <a href>) navigates on click; the panel still
          // opens on hover/focus. Only button-style triggers toggle on click.
          const href = trigger.getAttribute('href');
          if (trigger.tagName === 'A' && href && href !== '#') return;
          e.preventDefault();
          const isOpen = trigger.getAttribute('aria-expanded') === 'true';
          if (isOpen) this.close(item, trigger, panel);
          else this.open(item, trigger, panel);
        });
      });
    }

    open(item, trigger, panel) {
      clearTimeout(this.closeTimer);
      if (this.openItem && this.openItem !== item) this.closeAll();
      this.openItem = item;
      // Hold the solid (white) header bar open for as long as a panel is open. On
      // hover-out this lets the panel slide up/fade out FIRST, then the bar fades
      // after it (see close()). CSS reveal rules also honour .has-open-panel.
      this.classList.add('has-open-panel');
      panel.hidden = false;
      // next frame so the unhidden panel can transition
      requestAnimationFrame(() => {
        item.classList.add('is-open');
        trigger.setAttribute('aria-expanded', 'true');
        if (window.EZCarousel && panel.querySelector('[data-ez-carousel]')) {
          const c = panel.querySelector('[data-ez-carousel]');
          if (c.__ezCarousel) c.__ezCarousel.render();
        }
      });
    }

    close(item, trigger, panel) {
      item.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      if (this.openItem === item) this.openItem = null;
      // Let the panel finish its slide-up/fade-out (0.24s), THEN hide it and release
      // the held header background — so on hover-out the mega/dropdown disappears
      // first and the white bar fades out after it. Skip the bg release if another
      // panel reopened in the meantime (openItem set again before this fires).
      setTimeout(() => {
        panel.hidden = true;
        if (!this.openItem) this.classList.remove('has-open-panel');
      }, 260);
    }

    scheduleClose() {
      clearTimeout(this.closeTimer);
      this.closeTimer = setTimeout(() => this.closeAll(), HOVER_CLOSE_DELAY);
    }

    closeAll() {
      this.querySelectorAll('[data-mega].is-open, [data-dropdown].is-open').forEach((item) => {
        const trigger = item.querySelector('[data-mega-trigger], [data-dropdown-trigger]');
        const panel = item.querySelector('[data-mega-panel], [data-dropdown-panel]');
        if (trigger && panel) this.close(item, trigger, panel);
      });
      this.openItem = null;
    }

    bindSearch() {
      const toggle = this.querySelector('[data-search-toggle]');
      const close = this.querySelector('[data-search-close]');
      const input = this.querySelector('[data-search-input]');
      if (!toggle) return;

      const setSearching = (on) => {
        this.classList.toggle('is-searching', on);
        toggle.setAttribute('aria-expanded', String(on));
        if (on && input) {
          // focus once the input has transitioned in
          requestAnimationFrame(() => input.focus());
        }
      };
      toggle.addEventListener('click', () => setSearching(!this.classList.contains('is-searching')));
      if (close) close.addEventListener('click', () => { setSearching(false); toggle.focus(); });
      this.__setSearching = setSearching;
    }

    bindDrawer() {
      const toggle = this.querySelector('[data-drawer-toggle]');
      const drawer = document.querySelector('[data-drawer]');
      if (!toggle || !drawer) return;
      const close = drawer.querySelector('[data-drawer-close]');
      const setOpen = (open) => {
        drawer.hidden = !open;
        drawer.classList.toggle('is-open', open);
        toggle.setAttribute('aria-expanded', String(open));
        document.documentElement.classList.toggle('ez-drawer-open', open);
      };
      toggle.addEventListener('click', () => setOpen(drawer.hidden));
      if (close) close.addEventListener('click', () => setOpen(false));

      drawer.querySelectorAll('[data-accordion]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const sub = btn.nextElementSibling;
          const open = btn.getAttribute('aria-expanded') === 'true';
          btn.setAttribute('aria-expanded', String(!open));
          if (sub) sub.hidden = open;
        });
      });
    }
  }

  if (!customElements.get('ez-header')) {
    customElements.define('ez-header', EZHeader);
  }
})();
